import { useState } from 'react';
import { useHref } from 'react-router-dom';
import { Button } from '@core/components/Button';
import { Checkbox } from '@core/components/Checkbox';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ConsentScreenProps {
  onAccept: (privacyConsent: boolean, marketingConsent: boolean) => Promise<void>;
  venueSlug: string;
}

export function ConsentScreen({ onAccept, venueSlug }: ConsentScreenProps) {
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // Пути документов — от корня роутера входа: в мини-аппе /legal/…, в PWA /<слаг>/legal/….
  const privacyHref = useHref('/legal/privacy-policy');
  const termsHref = useHref('/legal/terms');

  const handleAccept = async () => {
    if (!privacyConsent) {
      return; // Privacy consent is required
    }

    setLoading(true);
    try {
      await onAccept(privacyConsent, marketingConsent);
    } catch (error) {
      console.error('Failed to grant consent:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-page flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card rounded-brand shadow-lg p-6 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-text-main">Добро пожаловать!</h1>
          <p className="text-text-muted text-sm">
            Для использования программы лояльности необходимо ваше согласие
          </p>
        </div>

        {/* Personal Data Info (inline) */}
        <div className="rounded-lg bg-page/50 p-4 space-y-3">
          <h2 className="text-sm font-semibold text-text-main">
            Какие данные мы обрабатываем
          </h2>
          <ul className="space-y-1 text-xs text-text-muted">
            <li>• Имя; при входе через Telegram — также фамилия и username</li>
            <li>• Номер телефона (вход без Telegram или добровольная привязка)</li>
            <li>• История покупок и начислений баллов</li>
          </ul>

          {/* Expandable details */}
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-xs text-primary font-medium"
          >
            {showDetails ? 'Свернуть' : 'Подробнее'}
            {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showDetails && (
            <div className="space-y-3 pt-2 border-t border-border text-xs text-text-muted">
              <div>
                <p className="font-semibold text-text-main mb-1">Цели обработки</p>
                <ul className="space-y-0.5">
                  <li>• Начисление и списание бонусных баллов</li>
                  <li>• Идентификация в программе лояльности</li>
                  <li>• Персонализация предложений</li>
                  <li>• Уведомления о транзакциях и бонусах</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-text-main mb-1">Ваши права (152-ФЗ)</p>
                <ul className="space-y-0.5">
                  <li>• Получать информацию об обработке ваших данных</li>
                  <li>• Требовать уточнения или удаления данных</li>
                  <li>• Отозвать согласие в любой момент</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-text-main mb-1">Защита данных</p>
                <ul className="space-y-0.5">
                  <li>• Шифрование при передаче (HTTPS/TLS)</li>
                  <li>• Хранение в защищённых дата-центрах</li>
                  <li>• Данные не передаются третьим лицам без согласия</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Privacy Consent (Required) */}
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={privacyConsent}
              onChange={(e) => setPrivacyConsent(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-text-main">
              Я согласен(на) на обработку персональных данных
              в соответствии с 152-ФЗ <span className="text-red-500">*</span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-text-main">
              Я согласен(на) на получение рекламных рассылок и спецпредложений
              в соответствии с 38-ФЗ
            </span>
          </label>
        </div>

        {/* Accept Button */}
        <Button
          onClick={handleAccept}
          disabled={!privacyConsent || loading}
          className="w-full"
        >
          {loading ? 'Обработка...' : 'Принять и продолжить'}
        </Button>

        {/* Required Field Note */}
        <p className="text-xs text-text-muted text-center">
          <span className="text-red-500">*</span> Обязательное поле
        </p>

        {/* Legal Documents Links */}
        <div className="text-center space-y-2 pt-4 border-t border-border">
          <p className="text-xs text-text-muted">Полные тексты документов:</p>
          <div className="flex justify-center gap-4">
            <a
              href={`${privacyHref}?venue=${encodeURIComponent(venueSlug)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary underline"
            >
              Политика конфиденциальности
            </a>
            <a
              href={`${termsHref}?venue=${encodeURIComponent(venueSlug)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary underline"
            >
              Соглашение
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
